/**
 * 감자시험 도전 횟수 공용 규칙 (JCG-3865).
 *
 * 감자시험(`/theory|coding/<과목>/<slug>-gamja-exam`)은 풀 때마다 문제가 랜덤으로 출제된다.
 * 출제된 세트의 모든 문제를 채점하면 "도전" 1회로 센다. 같은 세트는 재채점·새로고침 복원·문제 유지로
 * 다시 채점 완료 상태가 되어도 한 번만 센다. 새로고침·"다시 풀기"로 새 세트가 출제되면 새 도전이다.
 *
 * 세트 식별자(토큰)는 출제할 때 브라우저에 저장하고, 서버 RPC 는 사용자·회차·경로별 마지막 토큰으로
 * 중복 요청을 거른다(`record_gamja_exam_completion`).
 */

const GAMJA_EXAM_PATH_PATTERN =
  /^\/(?:theory|coding)\/[a-z0-9-]+\/[a-z0-9-]+-gamja-exam$/;
const SET_TOKEN_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const SET_STORAGE_PREFIX = "gamja-exam-set-";

/** 감자시험 페이지 경로면 저장 키로 쓸 경로(끝 슬래시 제거)를, 아니면 null 을 돌려준다. */
export function gamjaExamSlugFromPath(
  pathname: string | null | undefined,
): string | null {
  if (!pathname) return null;
  const path = pathname.replace(/\/+$/, "");
  return GAMJA_EXAM_PATH_PATTERN.test(path) ? path : null;
}

export function isGamjaExamSetToken(value: unknown): value is string {
  return typeof value === "string" && SET_TOKEN_PATTERN.test(value);
}

// ── 세트 식별자 저장 (클라이언트 전용) ─────────────────────────────────────────

export interface GamjaExamSetRecord {
  /** 세트의 문제 ID(정렬). 문제 유지의 표시 순서 변경과 무관하게 같은 세트를 알아본다. */
  problemIds: string[];
  token: string;
  /** 서버에 이 세트의 도전 1회를 기록했는지 */
  counted: boolean;
}

const getSetStorageKey = (storagePath: string) =>
  `${SET_STORAGE_PREFIX}${storagePath}`;

const sortedIds = (problemIds: string[]) => [...problemIds].sort();

const isSameSet = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

function createSetToken(): string {
  // randomUUID 는 보안 컨텍스트에서만 있다(LAN IP 로 띄운 개발 서버 등 대비).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readSetRecord(storagePath: string): GamjaExamSetRecord | null {
  try {
    const raw = localStorage.getItem(getSetStorageKey(storagePath));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as Partial<GamjaExamSetRecord>;
    if (
      !Array.isArray(record.problemIds) ||
      !record.problemIds.every((id) => typeof id === "string") ||
      !isGamjaExamSetToken(record.token)
    ) {
      return null;
    }
    return {
      problemIds: record.problemIds,
      token: record.token,
      counted: record.counted === true,
    };
  } catch {
    return null;
  }
}

function writeSetRecord(storagePath: string, record: GamjaExamSetRecord): void {
  try {
    localStorage.setItem(getSetStorageKey(storagePath), JSON.stringify(record));
  } catch {
    // localStorage 접근 불가 시 서버의 마지막 토큰 비교만으로 중복을 거른다.
  }
}

/** 새 세트를 출제했을 때 호출한다. 직전 세트와 문제가 같아도 새 도전으로 센다. */
export function startGamjaExamSet(
  storagePath: string,
  problemIds: string[],
): GamjaExamSetRecord {
  const record: GamjaExamSetRecord = {
    problemIds: sortedIds(problemIds),
    token: createSetToken(),
    counted: false,
  };
  writeSetRecord(storagePath, record);
  return record;
}

/**
 * 현재 화면 세트의 기록을 돌려준다. 저장된 세트와 다르면(배포 전 출제분·저장 손상) 새로 만든다.
 * 새로고침 복원·문제 유지·리사이즈 리마운트는 출제 시 저장한 기록을 그대로 이어 쓴다.
 */
export function getGamjaExamSet(
  storagePath: string,
  problemIds: string[],
): GamjaExamSetRecord {
  const stored = readSetRecord(storagePath);
  if (stored && isSameSet(stored.problemIds, sortedIds(problemIds))) {
    return stored;
  }
  return startGamjaExamSet(storagePath, problemIds);
}

export function markGamjaExamSetCounted(storagePath: string, token: string): void {
  const stored = readSetRecord(storagePath);
  if (!stored || stored.token !== token) return;
  writeSetRecord(storagePath, { ...stored, counted: true });
}
